import { makeObservable, computed, observable, action } from 'mobx';
import { flatten as _flatten } from 'lodash';
import { CustomerAvailabilityHour } from '@bringg/types';
import { DraggableItem } from '@bringg-frontend/draggable-list-selector';

import type RoutesPlannerStore from '../../stores/routes-planner-store';
import { TABLE_COLUMNS, TABLE_STATIC_COLUMNS } from '../../components/planner-table/table-columns';
interface TableStoreProps {
	customerAvailabilityHours: { [customerId: string]: CustomerAvailabilityHour[] };
}

const localStorageName = 'routesPlannerTableColumns';

interface PresentedColumn {
	id: string;
	priority: number;
	condition?: string;
}
export default class TableStore {
	rootStore: RoutesPlannerStore;
	skipTablePageReset: boolean;
	taskIdOfActiveDraggableItem: number;
	taskIdOfDroppedItem: number;
	customerAvailabilityHours: { [customerId: string]: CustomerAvailabilityHour[] };
	presentedColumns: { [key: string]: PresentedColumn };
	defaultColumns: { [key: string]: { id: string; priority: number; title: string; condition: string } };

	constructor(rootStore: RoutesPlannerStore, mapProps: TableStoreProps) {
		this.skipTablePageReset = false;
		this.taskIdOfActiveDraggableItem = null;
		this.taskIdOfDroppedItem = null;
		this.customerAvailabilityHours = mapProps.customerAvailabilityHours;

		this.rootStore = rootStore;

		const defaultColumns = { ...TABLE_COLUMNS };

		if (this.rootStore.customAttributes.length) {
			this.rootStore.customAttributes.forEach((attribute, i) => {
				defaultColumns[attribute.id] = {
					id: attribute.id,
					priority: Object.keys(TABLE_COLUMNS).length + Object.keys(TABLE_STATIC_COLUMNS).length + i + 1,
					title: attribute.name,
					condition: ''
				};
			});
		}

		this.defaultColumns = defaultColumns;

		const tableColumns: { [key: string]: PresentedColumn } =
			JSON.parse(localStorage.getItem(localStorageName)) || TABLE_COLUMNS;

		const presentedColumns = {};
		Object.values(tableColumns).forEach(column => {
			if (!column.condition || rootStore.allConfiguration[column.condition]) {
				presentedColumns[column.id] = { id: column.id, priority: column.priority };
			}
		});
		this.presentedColumns = presentedColumns;

		makeObservable(this, {
			skipTablePageReset: observable,
			taskIdOfActiveDraggableItem: observable,
			customerAvailabilityHours: observable,
			presentedColumns: observable,
			totalRuns: computed,
			tasks: computed,
			translations: computed,
			setSkipTablePageReset: action,
			setTaskIdOfActiveDraggableItem: action,
			setPresentedColumns: action
		});
	}

	get totalRuns() {
		return Object.keys(this.rootStore.routesById).length;
	}

	get tasks() {
		return _flatten(Object.values(this.rootStore.runToTasksInPresentedDate));
	}

	get translations() {
		return this.rootStore.translations.routesTableTranslations;
	}

	toggleRuns = (runIds: number[], show: boolean) => {
		const { setRunsToDisplay } = this.rootStore;

		setRunsToDisplay(runIds, { show });
	};

	onDraggableItemDropped(taskId: number) {
		this.setTaskIdOfDroppedItem(taskId);
		this.setTaskIdOfActiveDraggableItem(null);
	}

	setSkipTablePageReset = (skip: boolean) => {
		this.skipTablePageReset = skip;
	};

	setTaskIdOfDroppedItem = (taskId: number) => {
		this.taskIdOfDroppedItem = taskId;
	};

	setTaskIdOfActiveDraggableItem = (taskId: number) => {
		this.taskIdOfActiveDraggableItem = taskId;
	};

	setColumnsToLocalStorage(presentedColumns: { [key: string]: PresentedColumn }) {
		localStorage.setItem(localStorageName, JSON.stringify(presentedColumns));
	}

	setPresentedColumns = (columns: DraggableItem[]) => {
		const presentedColumns = {};
		columns.forEach((column, index) => {
			const condition = TABLE_COLUMNS[column.id]?.condition;
			if (!condition || this.rootStore.allConfiguration[condition])
				presentedColumns[column.id] = {
					id: column.id,
					priority: index + Object.keys(TABLE_STATIC_COLUMNS).length + 1
				};
		});
		this.setColumnsToLocalStorage(presentedColumns);
		this.presentedColumns = presentedColumns;
	};
}
